class VivazzSizeSheet extends HTMLElement {
  #controller;
  #busy = false;
  #observer;
  #returnFocus;
  #restockFocus;
  #restockVariant;
  #restockController;
  #restockProvider;
  #restockBusy = false;
  #nativeFormsCleanup;
  #nativeFormsCompleted;

  get picker() {
    return document.querySelector(`variant-picker[data-section-id="${CSS.escape(this.dataset.sectionId)}"][data-product-id="${CSS.escape(this.dataset.productId)}"]`);
  }
  get select() { return this.picker?.querySelector('[data-vivazz-size-select] select'); }
  get dialog() { return this.querySelector('dialog'); }
  get restockDialog() { return this.querySelector('[data-restock-dialog]'); }
  get mainButton() { return document.getElementById(`BuyButtons-ProductForm-${this.dataset.sectionId}`)?.querySelector('[name="add"]'); }

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    if (this.dataset.restockProvider === 'stoq') this.registerRestockProvider(this.#subscribeStoq);
    document.addEventListener('click', this.#documentClick, { signal });
    document.addEventListener('invalid', this.#invalid, { capture: true, signal });
    document.addEventListener('variant:selected', this.#onSelected, { signal });
    document.addEventListener('variant:update', this.#onUpdated, { signal });
    document.addEventListener('vivazz:size-choice', (event) => {
      if (event.target === this.picker) this.#refresh();
    }, { signal });
    document.addEventListener('vivazz:variant-error', this.#onError, { signal });
    this.addEventListener('click', this.#click, { signal });
    this.querySelector('[data-restock-form]')?.addEventListener('submit', this.#submitRestock, { signal });
    this.restockDialog?.addEventListener('close', () => {
      this.#restockController?.abort();
      this.#nativeFormsCleanup?.();
      const target = this.#restockFocus?.isConnected && !this.#restockFocus.hidden ? this.#restockFocus : this.picker?.querySelector('[data-size-sheet-open]');
      target?.focus({ preventScroll: true });
    }, { signal });
    this.dialog.addEventListener('close', () => {
      this.picker?.querySelector('[data-size-sheet-open]')?.setAttribute('aria-expanded', 'false');
      this.#returnFocus?.isConnected && this.#returnFocus.focus({ preventScroll: true });
    }, { signal });
    this.#observer = new MutationObserver(() => this.#refresh());
    if (this.picker) this.#observer.observe(this.picker, { childList: true, subtree: true });
    queueMicrotask(() => this.#refresh());
  }
  disconnectedCallback() {
    this.dialog?.close();
    this.restockDialog?.close();
    this.#restockController?.abort();
    document.querySelectorAll(`[data-restock-owner="${CSS.escape(this.dialog.id)}"]`).forEach((button) => button.remove());
    this.mainButton?.removeAttribute('data-restock-replaced');
    this.closest('.shopify-section')?.querySelector('sticky-add-to-cart [data-restock-replaced]')?.removeAttribute('data-restock-replaced');
    this.#controller?.abort();
    this.#nativeFormsCleanup?.();
    this.#observer?.disconnect();
  }
  #documentClick = (event) => {
    const notify = event.target instanceof Element && event.target.closest('[data-restock-open]');
    if (notify?.dataset.restockOwner === this.dialog.id) {
      event.preventDefault();
      this.#openRestock(this.select?.value, notify);
      return;
    }
    const trigger = event.target instanceof Element && event.target.closest('[data-size-sheet-open]');
    if (trigger && this.picker?.contains(trigger)) { event.preventDefault(); this.open(trigger); }
  };
  #invalid = (event) => {
    if (event.target === this.select) { event.preventDefault(); this.open(this.picker.querySelector('[data-size-sheet-open]')); }
  };
  #onSelected = (event) => {
    if (event.target !== this.picker) return;
    this.#busy = !event.detail.variant;
    this.#refresh();
  };
  #onUpdated = (event) => {
    if (event.target !== this.picker) return;
    this.#busy = false;
    queueMicrotask(() => this.#refresh());
  };
  #onError = (event) => {
    if (event.target !== this.picker) return;
    if (this.picker.dataset.instantVariantId) {
      this.#busy = false;
      this.#refresh();
      return;
    }
    this.#busy = true;
    this.querySelector('[data-sheet-status]').textContent = this.dataset.error;
    this.querySelector('[data-sheet-add]').disabled = true;
  };
  open(trigger) {
    if (!this.select) return;
    this.#returnFocus = trigger;
    this.#refresh();
    if (!this.dialog.open) this.dialog.showModal();
    trigger?.setAttribute('aria-expanded', 'true');
  }
  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-restock-close], [data-restock-continue]')) { this.restockDialog.close(); return; }
    const notify = event.target.closest('[data-sheet-notify]');
    if (notify) { this.#openRestock(notify.dataset.sheetNotify, this.picker?.querySelector('[data-size-sheet-open]')); return; }
    if (event.target === this.restockDialog) {
      const r = this.restockDialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.restockDialog.close();
      return;
    }
    if (event.target.closest('[data-sheet-close]')) { this.dialog.close(); return; }
    if (event.target === this.dialog) {
      const r = this.dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      return;
    }
    const option = event.target.closest('[data-sheet-option]');
    if (option && this.select) {
      this.select.value = option.dataset.sheetOption;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    if (event.target.closest('[data-sheet-add]')) {
      const variant = this.picker?.resolveVariant?.();
      if (variant?.available === false && this.select?.value && !this.#busy) {
        this.#openRestock(this.select.value, this.picker.querySelector('[data-size-sheet-open]'));
        return;
      }
      const button = this.mainButton;
      if (this.#busy || !this.select?.value || !button || button.disabled) return;
      if (!button.form?.reportValidity()) return;
      this.dialog.close();
      button.click();
    }
  };
  #refresh() {
    const select = this.select;
    if (!select) return;
    const wrapper = select.closest('.variant-option__select-wrapper');
    const trigger = wrapper?.querySelector('[data-size-sheet-open]');
    if (!trigger) return;
    const text = select.value || this.dataset.choose;
    const triggerText = trigger.querySelector('[data-size-sheet-label]');
    if (triggerText.textContent !== text) triggerText.textContent = text;
    trigger.hidden = false;
    wrapper.dataset.vivazzSheetReady = 'true';
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    const color = this.picker.querySelector('[data-vivazz-color] .variant-option__swatch-value')?.textContent.trim() || '';
    this.querySelector('[data-sheet-color]').textContent = color;
    const container = this.querySelector('[data-sheet-options]');
    const focused = this.contains(document.activeElement) ? document.activeElement?.dataset.sheetOption : null;
    const rows = Array.from(select.options).filter(o => o.dataset.optionValueId).map(o => {
      const row = document.createElement('div');
      row.className = 'vivazz-size-sheet__row';
      const localData = this.picker.variantData;
      const available = localData ? this.picker.resolveVariant(o.value)?.available === true : o.dataset.optionAvailable !== 'false';
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.sheetOption = o.value;
      button.className = 'vivazz-size-sheet__option';
      button.disabled = !available;
      button.setAttribute('aria-pressed', String(o.selected));
      const label = document.createElement('span'); label.textContent = o.value;
      const mark = document.createElement('span'); mark.setAttribute('aria-hidden', 'true'); mark.textContent = o.selected ? '✓' : '';
      button.append(label, mark); row.append(button);
      if (!available) {
        row.classList.add('is-unavailable');
        const request = document.createElement('button');
        request.type = 'button';
        request.className = 'vivazz-size-sheet__notify';
        request.dataset.sheetNotify = o.value;
        request.setAttribute('aria-haspopup', 'dialog');
        request.setAttribute('aria-controls', this.restockDialog.id);
        const notifyText = document.createElement('span');
        notifyText.textContent = this.dataset.notify;
        request.append(label, notifyText);
        button.remove();
        request.disabled = !this.picker?.resolveVariant?.(o.value);
        const bell = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        bell.setAttribute('viewBox', '0 0 24 24');
        bell.setAttribute('width', '18');
        bell.setAttribute('height', '18');
        bell.setAttribute('fill', 'none');
        bell.setAttribute('aria-hidden', 'true');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '1.5');
        bell.append(path); request.append(bell);
        request.setAttribute('aria-label', `${this.dataset.notify}: ${o.value}, ${color}`);
        row.append(request);
      }
      return row;
    });
    container.replaceChildren(...rows);
    if (focused && this.dialog.open) container.querySelector(`[data-sheet-option="${CSS.escape(focused)}"]`)?.focus({ preventScroll: true });
    const available = this.picker.variantData ? this.picker.resolveVariant()?.available === true : select.selectedOptions[0]?.dataset.optionAvailable !== 'false';
    const add = this.querySelector('[data-sheet-add]');
    const canNotify = this.picker?.resolveVariant?.()?.available === false;
    add.disabled = this.#busy || !select.value || (!available && !canNotify) || (available && (!this.mainButton || this.mainButton.disabled));
    add.textContent = this.#busy ? this.dataset.updating : !select.value ? this.dataset.choose : !available ? this.dataset.restockButton : this.dataset.add;
    this.querySelector('[data-sheet-status]').textContent = this.#busy ? this.dataset.updating : '';
    this.#syncRestockButtons(Boolean(select.value && canNotify && !this.#busy));
  }

  #subscribeStoq = async (payload, { signal }) => {
    const response = await fetch('https://app.stoqapp.com/api/v1/intents.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Shop-Domain': this.dataset.shopDomain },
      credentials: 'omit',
      signal,
      body: JSON.stringify({
        intent: { shopify_variant_id: Number(payload.variantId), shopify_product_id: Number(payload.productId), channel: 'email', quantity: 1, source: 'api' },
        customer: { email: payload.email, accepts_marketing: false, locale: payload.locale },
        product: { title: payload.productTitle, variant_title: payload.variantTitle },
      }),
    });
    const body = await response.json();
    const accepted = body.intents?.some((intent) => String(intent.shopify_variant_id) === payload.variantId && intent.id);
    return { success: response.status === 201 && !body.errors?.length && Boolean(accepted) };
  };
  registerRestockProvider(provider) {
    if (typeof provider !== 'function') throw new TypeError('A stock-alert subscription provider is required.');
    this.#restockProvider = provider;
  }
  #syncRestockButtons(show) {
    const sticky = this.closest('.shopify-section')?.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
    for (const original of [this.mainButton, sticky].filter(Boolean)) {
      let notify = original.parentElement.querySelector(`[data-restock-owner="${CSS.escape(this.dialog.id)}"]`);
      if (!notify && show) {
        notify = document.createElement('button');
        notify.type = 'button';
        notify.className = `${original.className} vivazz-restock-trigger`;
        notify.dataset.restockOpen = '';
        notify.dataset.restockOwner = this.dialog.id;
        notify.setAttribute('aria-haspopup', 'dialog');
        notify.setAttribute('aria-controls', this.restockDialog.id);
        notify.textContent = this.dataset.restockButton;
        original.after(notify);
      }
      original.toggleAttribute('data-restock-replaced', show);
      if (notify) notify.hidden = !show;
    }
  }
  #openRestock(size, trigger) {
    const variant = size && this.picker?.resolveVariant?.(size);
    if (!variant || variant.available !== false) {
      this.open(this.picker?.querySelector('[data-size-sheet-open]'));
      return;
    }
    this.#restockController?.abort();
    this.#restockBusy = false;
    this.#restockVariant = variant;
    this.#restockFocus = trigger;
    this.dialog.close();
    const dialog = this.restockDialog;
    const form = dialog.querySelector('[data-restock-form]');
    form.reset();
    form.hidden = false;
    form.removeAttribute('aria-busy');
    dialog.querySelector('[data-restock-entry]').hidden = false;
    dialog.querySelector('[data-restock-success]').hidden = true;
    dialog.querySelector('[data-restock-continue]').hidden = true;
    dialog.querySelector('[data-restock-error]').textContent = '';
    const submit = dialog.querySelector('[data-restock-submit]');
    submit.disabled = false;
    submit.textContent = this.dataset.notify;
    dialog.setAttribute('aria-labelledby', `${dialog.id}-title`);
    const color = this.picker.querySelector('[data-vivazz-color] .variant-option__swatch-value')?.textContent.trim() || '';
    dialog.querySelector('[data-restock-selection]').textContent = [color, size].filter(Boolean).join(' · ');
    const image = dialog.querySelector('.vivazz-restock__image');
    const source = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
    if (image) {
      image.dataset.defaultSrc ||= image.getAttribute('src');
      image.dataset.defaultAlt ||= image.alt;
      image.removeAttribute('srcset');
      image.src = source || image.dataset.defaultSrc;
      image.alt = `${this.dataset.productTitle} — ${color}`;
    }
    if (!dialog.open) dialog.showModal();
    if (this.dataset.restockProvider === 'shopify-forms') this.#openNativeForms(variant);
  }
  #openNativeForms(variant) {
    this.#nativeFormsCleanup?.();
    const dialog = this.restockDialog;
    const slot = dialog.querySelector('[data-restock-native-slot]');
    const status = dialog.querySelector('[data-restock-native-status]');
    dialog.querySelector('[data-restock-form]').hidden = true;
    slot.hidden = false;
    status.hidden = false;
    status.textContent = 'Loading your stock alert…';
    if (this.#nativeFormsCompleted) {
      slot.hidden = true;
      status.textContent = 'Your previous request was saved. Open this size in a fresh page to request another alert.';
      const link = document.createElement('a');
      const next = new URL(this.dataset.productUrl, location.origin);
      next.searchParams.set('variant', String(variant.id));
      link.href = next.href;
      link.textContent = 'Open this size';
      status.append(document.createElement('br'), link);
      dialog.querySelector('[data-restock-continue]').hidden = false;
      return;
    }
    const url = new URL(this.dataset.productUrl, location.origin);
    url.searchParams.set('variant', String(variant.id));
    const expected = url.href;
    let observer, poll, root, submitted = false;
    const controller = new AbortController();
    const cleanup = () => { clearInterval(poll); observer?.disconnect(); controller.abort(); };
    this.#nativeFormsCleanup = cleanup;
    const sync = () => {
      if (!dialog.open || controller.signal.aborted) return;
      const field = root?.getElementById('custom#product_link');
      const nativeForm = field?.closest('form');
      if (field && nativeForm) {
        if (field.value !== expected) {
          Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set.call(field, expected);
          field.dispatchEvent(new Event('input', {bubbles: true, composed: true}));
          field.dispatchEvent(new Event('change', {bubbles: true, composed: true}));
        }
        field.readOnly = true;
        field.parentElement.hidden = true;
        status.hidden = true;
        if (!nativeForm.dataset.vivazzBound) {
          nativeForm.dataset.vivazzBound = 'true';
          nativeForm.addEventListener('submit', (event) => {
            if (field.value !== expected || !dialog.open) {
              event.preventDefault(); event.stopImmediatePropagation();
              status.hidden = false;
              status.textContent = 'Please reopen this alert to confirm your selected size.';
              return;
            }
            submitted = true;
          }, {capture: true, signal: controller.signal});
          controller.signal.addEventListener('abort', () => delete nativeForm.dataset.vivazzBound, {once: true});
        }
      } else if (submitted && root?.textContent.includes("You're on the list.") && root.textContent.includes('Your request has been saved.')) {
        this.#nativeFormsCompleted = true;
        slot.hidden = true;
        status.hidden = true;
        dialog.querySelector('[data-restock-entry]').hidden = true;
        dialog.querySelector('[data-restock-success]').hidden = false;
        dialog.querySelector('[data-restock-continue]').hidden = false;
        const heading = dialog.querySelector('[data-restock-success] h2');
        dialog.setAttribute('aria-labelledby', heading.id);
        heading.focus({preventScroll: true});
        cleanup();
      }
    };
    let tries = 0;
    const attach = () => {
      const container = document.querySelector(`[data-forms-id="forms-root-${this.dataset.restockFormId}"]`);
      if (container && !slot.contains(container)) {
        const section = container.closest('.shopify-section');
        if (section) section.hidden = true;
        slot.append(container);
      }
      root = container?.querySelector('shopify-forms-embed')?.shadowRoot;
      if (root && root.getElementById('custom#product_link')) {
        clearInterval(poll);
        if (!root.querySelector('[data-vivazz-forms-style]')) {
          const style = document.createElement('style');
          style.dataset.vivazzFormsStyle = '';
          style.textContent = '[data-sizing="form-wrapper"]{padding:0!important;box-shadow:none!important;background:transparent!important;display:block!important} section[class*="formHeader"]{display:none!important} div[class*="imageLoading"]{display:none!important} input{border-radius:0!important;background:#f5f5f5!important} button[type="submit"]{box-sizing:border-box!important;display:flex!important;align-items:center!important;justify-content:center!important;width:100%!important;height:54px!important;min-height:54px!important;padding:0 24px!important;border:0!important;border-radius:999px!important;background:#111!important;color:#fff!important;font-size:16px!important;line-height:1.2!important;font-weight:600!important;text-align:center!important} [hidden]{display:none!important}';
          root.append(style);
        }
        // The outer native dialog handles focus and modality.
        const innerDialog = root.querySelector('[role="dialog"]');
        innerDialog?.setAttribute('role', 'group');
        innerDialog?.removeAttribute('aria-modal');
        observer = new MutationObserver(sync);
        observer.observe(root, {childList:true, subtree:true});
        sync();
      } else if (++tries > 50) {
        cleanup();
        status.textContent = 'The stock-alert form could not load. Please refresh and try again.';
      }
    };
    poll = setInterval(attach, 200);
    attach();
  }
  #submitRestock = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (this.#restockBusy || !form.reportValidity()) return;
    const dialog = this.restockDialog;
    const error = dialog.querySelector('[data-restock-error]');
    const email = form.elements.namedItem('email').value.trim();
    const variant = this.#restockVariant;
    error.textContent = '';
    if (!variant?.id) { error.textContent = dialog.dataset.variantError; return; }
    const submit = dialog.querySelector('[data-restock-submit]');
    const controller = new AbortController();
    this.#restockController = controller;
    this.#restockBusy = true;
    submit.disabled = true;
    submit.textContent = dialog.dataset.submitting;
    form.setAttribute('aria-busy', 'true');
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      if (!this.#restockProvider) throw new Error('Stock-alert provider not connected.');
      const payload = {
        email,
        productId: this.dataset.productId,
        variantId: String(variant.id),
        productTitle: this.dataset.productTitle,
        variantTitle: variant.title,
        locale: document.documentElement.lang || 'en',
        marketingConsent: false,
      };
      const result = await Promise.race([
        this.#restockProvider(payload, { signal: controller.signal }),
        new Promise((_, reject) => controller.signal.addEventListener('abort', () => reject(new Error('Request interrupted.')), { once: true })),
      ]);
      if (controller.signal.aborted || !dialog.open) return;
      if (result?.success !== true) throw new Error('Subscription was not confirmed.');
      form.reset();
      form.hidden = true;
      dialog.querySelector('[data-restock-entry]').hidden = true;
      dialog.querySelector('[data-restock-success]').hidden = false;
      dialog.querySelector('[data-restock-continue]').hidden = false;
      const heading = dialog.querySelector('[data-restock-success] h2');
      dialog.setAttribute('aria-labelledby', heading.id);
      heading.focus({ preventScroll: true });
    } catch {
      if (dialog.open) error.textContent = dialog.dataset.error;
    } finally {
      clearTimeout(timeout);
      if (this.#restockController === controller) {
        this.#restockBusy = false;
        submit.disabled = false;
        submit.textContent = this.dataset.notify;
        form.removeAttribute('aria-busy');
      }
    }
  };
}
if (!customElements.get('vivazz-size-sheet')) customElements.define('vivazz-size-sheet', VivazzSizeSheet);
